import type { PeekViewService } from '@affine/core/modules/peek-view';
import { I18n } from '@affine/i18n';
import { streamLainDisclosureStyles } from '@blocksuite/affine-shared/utils';
import { WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import type { Signal } from '@preact/signals-core';
import { css, html, nothing, unsafeCSS } from 'lit';
import { property } from 'lit/decorators.js';

import type { DocDisplayConfig } from '../ai-chat-chips';
import {
  type AiDocSourcePillItem,
  docSearchSourceStyles,
  renderDocSearchSourceList,
} from './doc-search-source';

interface DocSemanticSearchToolCall {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: { query: string };
}

interface DocSemanticSearchToolResult {
  type: 'tool-result';
  toolCallId: string;
  toolName: string;
  args: { query: string };
  result:
    | Array<DocSemanticSearchResultItem>
    | { success?: boolean; data: DocSemanticSearchResultItem[] };
}

type DocSemanticSearchResultItem = Omit<AiDocSourcePillItem, 'title'> & {
  content: string;
  title?: string;
};

export function pdfTitleFromSemanticContent(
  content: string | undefined
): string {
  const titleLine = content
    ?.split('\n')
    .find(line => line.trim().startsWith('Title:'));
  const title = titleLine
    ?.replace(/^Title:\s*/i, '')
    .replace(/,\s*page\s+\d+\s*$/i, '')
    .trim();
  return title && title !== 'PDF' ? title : '';
}

export class DocSemanticSearchResult extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    ${unsafeCSS(streamLainDisclosureStyles)}
    ${unsafeCSS(docSearchSourceStyles)}
  `;

  @property({ attribute: false })
  accessor data!: DocSemanticSearchToolCall | DocSemanticSearchToolResult;

  @property({ attribute: false })
  accessor width: Signal<number | undefined> | undefined;

  @property({ attribute: false })
  accessor docDisplayService!: DocDisplayConfig;

  @property({ attribute: false })
  accessor onOpenDoc!: (docId: string, sessionId?: string) => void;

  @property({ attribute: false })
  accessor peekViewService!: PeekViewService;

  renderToolCall() {
    const query = this.data.args?.query ?? '';
    return html`<div class="doc-search-activity">
      ${I18n.t('com.streamlain.ai.doc-search.semantic.searching', {
        query,
      })}
    </div>`;
  }

  renderToolResult() {
    if (this.data.type !== 'tool-result') {
      return nothing;
    }
    // Handle both direct array result and { success, data: [...] } wrapper
    const results: DocSemanticSearchResultItem[] = Array.isArray(
      this.data.result
    )
      ? this.data.result
      : Array.isArray(this.data.result?.data)
        ? this.data.result.data
        : [];
    const query =
      this.data.args?.query ||
      results.find(result => result.query?.trim())?.query ||
      '';
    const untitled = I18n.t('com.streamlain.ai.doc-search.untitled');
    const sourceResults = results.map(result => ({
      ...result,
      attachmentName:
        result.attachmentName || pdfTitleFromSemanticContent(result.content),
      title:
        result.sourceKind === 'pdf-page'
          ? result.attachmentName ||
            pdfTitleFromSemanticContent(result.content) ||
            untitled
          : (this.docDisplayService?.getTitle(result.docId) ?? untitled),
    }));

    return renderDocSearchSourceList(this, sourceResults, query);
  }

  protected override render() {
    const { data } = this;

    if (data.type === 'tool-call') {
      return this.renderToolCall();
    }
    if (data.type === 'tool-result') {
      return this.renderToolResult();
    }
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'doc-semantic-search-result': DocSemanticSearchResult;
  }
}
